import { ref, onMounted } from 'vue'
import type { KeyboardInfo } from '@capacitor/keyboard'
import { Keyboard } from '@capacitor/keyboard'
import { usePlatformInfo } from './usePlatformInfo'

type KeyboardCallback = (info?: KeyboardInfo) => void

export function useKeyboard() {
  const isOpen = ref(false)
  const keyboardOverlayHeight = ref(0)

  // Store custom callbacks
  const showCallbacks = ref<KeyboardCallback[]>([])
  const hideCallbacks = ref<KeyboardCallback[]>([])

  const { isIos, isAndroid } = usePlatformInfo()

  const handleKeyboardWillShow = (info: KeyboardInfo) => {
    if (isIos) {
      keyboardOverlayHeight.value = info.keyboardHeight
    }
    isOpen.value = true

    // Execute custom callbacks
    showCallbacks.value.forEach((callback) => callback(info))
  }

  const handleKeyboardWillHide = () => {
    if (isIos) {
      setTimeout(() => {
        keyboardOverlayHeight.value = 0
        isOpen.value = false
      }, 170)
    }
    if (isAndroid) {
      isOpen.value = false
    }

    // Execute custom callbacks
    hideCallbacks.value.forEach((callback) => callback())
  }

  const handleKeyboardDidHide = () => {
    if (isIos) {
      keyboardOverlayHeight.value = 0
    }
    isOpen.value = false
  }

  // Add custom callback for keyboard show events
  const addShowListener = (callback: KeyboardCallback) => {
    showCallbacks.value.push(callback)

    // Return cleanup function
    return () => {
      const index = showCallbacks.value.indexOf(callback)
      if (index > -1) {
        showCallbacks.value.splice(index, 1)
      }
    }
  }

  // Add custom callback for keyboard hide events
  const addHideListener = (callback: KeyboardCallback) => {
    hideCallbacks.value.push(callback)

    // Return cleanup function
    return () => {
      const index = hideCallbacks.value.indexOf(callback)
      if (index > -1) {
        hideCallbacks.value.splice(index, 1)
      }
    }
  }

  onMounted(() => {
    if (isIos || isAndroid) {
      void Keyboard.addListener('keyboardWillShow', handleKeyboardWillShow)
      void Keyboard.addListener('keyboardWillHide', handleKeyboardWillHide)
      void Keyboard.addListener('keyboardDidHide', handleKeyboardDidHide)
    }
  })

  return {
    isOpen,
    keyboardOverlayHeight,
    addShowListener,
    addHideListener,
  }
}
